<template>
  <div>
    <tf-button
      v-if="projectProductSoldStatus.toLowerCase() !== 'no policy sold'"
      v-branding-cta.secondary="primaryColor"
      size="small"
      :disabled="isSaving || (projectProductSoldStatus.toLowerCase() === 'new policy sold')"
      @click="toggleNotSoldState(true)"
      v-text="'Not sold'"
    />
    <template v-else-if="projectProductSoldStatus.toLowerCase() === 'no policy sold'">
      <span>Not sold</span>

      <bp-tooltip append-to-body>
        <icon
          slot="reference"
          class="icon-not-sold"
          name="custom-info"
        />
        <tf-button
          v-branding-cta.text="primaryColor"
          size="text-small"
          :disabled="isSaving"
          @click="toggleNotSoldState(false)"
          v-text="'Undo marked as not sold.'"
        />
      </bp-tooltip>
    </template>
  </div>
</template>

<script>
  // vuex
  import { mapGetters } from 'vuex';
  // Services
  import { setNoPolicySoldState } from '@/services/project';
  /**
   * "CtaNotSold" is a component that displays a "Not Sold" CTA that when clicked fires a mark as no policy sold patch call.
   * It then toggles into a state that displays "Not Sold" as text and an info icon. Upon hovering over the info icon, a tooltip appears that contains
   * an "Undo marked as not sold" cta that fires a mark as purchase decision pending patch call.
   *
   * @vuedoc
   * @exports CtaNotSold
   * @category Components/CTAs
   */
  export default {
    name: 'CtaNotSold',
    props: {
      /**
       * The ID of the project product that is being marked as Not Sold. Sent to the backend while initiating or reversing a "mark_as_no_policy_sold" call
       */
      projectProductId: {
        type: Number,
        default: null,
      },
      /**
       * The sold_status for the project product. If sold_status is "Not Sold", then the "Not Sold" button displays, otherwise if it is "no policy sold", then "Not Sold" text with a tooltip displays.
       */
      projectProductSoldStatus: {
        type: String,
        default: '',
      },
    },
    data: () => ({
      isSaving: false,
    }),
    computed: {
      ...mapGetters(['primaryColor']),
    },
    methods: {
      /**
       * Makes an API PATCH call for the project product and emits an event up to the parent
       * to update the sold_status in the store and database to "no policy sold" if a user has marked a Project Product as "Not Sold"
       * or back to the "not sold" default if a user has elected to undo any previous "Mark as Not Sold" action.
       *
       * @param {boolean} noPolicySold
       */
      toggleNotSoldState(noPolicySold) {
        // In our next iteration, this call may need to be repeated for each projectProductID that is an alternative of this base product.
        // Once completing this update, these projectProductIds may need to be obtained with a getIds call of some sort, instead of being passed as props.
        const action = noPolicySold ? 'not sold' : 'purchase decision pending';
        const soldStatus = noPolicySold ? 'No Policy Sold' : 'Not Sold';

        this.isSaving = true;

        setNoPolicySoldState(this.projectProductId, noPolicySold)
          .then(() => {
            this.$emit('updateProjectProductSoldStates', [{
              projectProductId: this.projectProductId,
              soldStatus,
            }]);
          })
          .catch(() => {
            this.$message({
              duration: 4200,
              message: `An error occurred when attempting to mark this project product as ${action}.`,
              showClose: true,
              type: 'error',
            });
          })
          .finally(() => {
            this.isSaving = false;
          });
      },
    },
  };
</script>

<style lang="scss" scoped>
  ::v-deep .icon-not-sold {
    position: relative;
    top: 1px;
    width: 14px;
    height: 14px;

    path {
      fill: $tf-dark-gray;
    }
  }
</style>
